<template>
    <div>
        <!-- Postcode -->
        <component
            :is="postcodeLookupField"
            ref="postcode"
            address-type="billing"
            v-model="address.postcode"
            @address-selected="updateSelectedAddress"
            />

        <!-- Address line 1 -->
         <div>
            <input-field
                class="mb-3"
                v-model="address.houseNumber"
                :mandatory="true"
                :is-valid="!errorHandler.has('houseNumber')"
                :message="$t(errorHandler.get('houseNumber'))"
                :label="$t('BILLING_ADDRESS_FORM_HOUSE_NUMBER')"/>
        </div>
        <div>
            <input-field
                class="mb-3"
                v-model="address.addressLine1"
                :is-valid="!errorHandler.has('billing_address_line_1')"
                :message="$t(errorHandler.get('billing_address_line_1'))"
                :label="$t('BILLING_ADDRESS_FORM_FIRST_LINE')"
                :mandatory="true"/>
        </div>

        <!-- Address line 2 -->
        <div>
            <input-field
                class="mb-3"
                v-model="address.addressLine2"
                :label="$t('BILLING_ADDRESS_FORM_SECOND_LINE')"/>
        </div>

        <!-- City -->
        <div>
            <input-field
                class="mb-3"
                v-model="address.city"
                :is-valid="!errorHandler.has('billing_address_city')"
                :message="$t(errorHandler.get('billing_address_city'))"
                :label="$t('BILLING_ADDRESS_FORM_TOWN')"/>
        </div>

        <div>
            <div class="state" v-if="getPauseIsCancel">
                <p class="required"> {{$t("DELIVERY_ADDRESS_STATE")}} </p>
                <Dropdown
                    v-model="address.state"
                    :inputStyle="'border-color: grey; width: 100%'"
                    :placeholder="$t('DELIVERY_ADDRESS_STATE')"
                    :items="states" />
            </div>
        </div>

        <!-- Countries address dropdown -->
        <div class="input">
            <label class="input__label"
                   v-text="$t('BILLING_ADDRESS_FORM_COUNTRY')"></label>
            <multiselect
                v-model="selectedCountry"
                track-by="iso"
                label="name"
                :placeholder="$t('PLEASE_SELECT_COUNTRY')"
                openDirection="below"
                :allow-empty="false"
                :show-labels="false"
                :options="countries"/>
            <p class="message"
               v-text="$t(errorHandler.get('billing_address_country'))"/>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import { Dropdown } from '@freddiesflowers/frontend-component-library';
import Countries from '@/resources/countries.json';
import ErrorResponseHandler from '@/helpers/ErrorResponseHandler';
import CountryService from '@/services/logistics/CountryService';
import InputField from '@/components/forms/fields/InputField';

export default {
    name: 'BillingAddressForm',
    data: () => {
        return {
            postcodeComponent: null,
            address: {
                houseNumber: null,
                addressLine1: null,
                addressLine2: null,
                city: null,
                postcode: null,
                country: null,
                state: null,
            },
            selectedCountry: null,
        };
    },
    props: {
        value: {
            required: true,
            type: Object,
        },
        errorHandler: {
            required: false,
            default: () => new ErrorResponseHandler(),
            validator: function(value) {
                return value instanceof ErrorResponseHandler;
            },
        },
    },
    mounted() {
        this.selectedCountry = this.countries.filter(item => item.iso === process.env.VUE_APP_COUNTRY_ISO)[0];
    },
    methods: {
        updateSelectedAddress(address) {
            this.address.houseNumber = address.houseNumber;
            this.address.addressLine1 = address.addressline1;
            this.address.addressLine2 = address.posttown;
            this.address.city = address.country;
            this.address.state = address.state;
            this.address.country = this.selectedCountry;
        },
    },
    computed: {
        ...mapGetters({
            getPauseIsCancel: 'subscription/getPauseIsCancel',
        }),
        postcodeLookupField() {
            const path = {
                'en': import('@/components/forms/fields/PostcodeLookupField'),
                'de': import('@/components/forms/fields/de/PostcodeLookupField'),
                'nl': import('@/components/forms/fields/de/PostcodeLookupField'),
                'en-US': import('@/components/forms/fields/de/PostcodeLookupField'),
            };
            path[process.env.VUE_APP_I18N_LOCALE].then(module => module.default).then(response => {
                this.postcodeComponent = response;
            });
            return this.postcodeComponent;
        },
        countries() {
            return CountryService.getCountries()
                .map((country) => {
                    return {
                        name: country.name,
                        iso: country.iso,
                    };
                });
        },
        states() {
            const USA = Countries.find((item) => item.iso === 'USA');
            return USA.states;
        },
    },
    watch: {
        address: {
            deep: true,
            handler(address) {
                this.$emit('input', address);
            },
        },
        value: {
            immediate: true,
            handler(value) {
                this.address.houseNumber = value.houseNumber;
                this.address.addressLine1 = value.addressLine1;
                this.address.addressLine2 = value.addressLine2;
                this.address.postcode = value.postcode;
                this.address.city = value.city;
                this.address.state = value.state;
                this.address.country = this.selectedCountry;
            },
        },
        selectedCountry: {
            handler(country) {
                this.address.country = country;
            },
            immediate: true,
        },
    },
    components: {
        InputField,
        Multiselect,
        Dropdown,
    },
};
</script>

<style lang="scss" scoped>
    .state {
        margin-bottom: 10px;
        padding: 0px 15px;
        text-align: left;
    }
    .required:after {
        content:" *";
        color: red;
        text-align: left;
    }
</style>
